/**
 * MapAuto — auto-generating + manually-arrangeable network/inventory map (v2).
 *
 * Cytoscape.js engine. Two modes:
 *  • «Авто» — fcose force layout, the map arranges itself.
 *  • «Моя расстановка» — drag nodes/zones; positions persist (MapLayout store).
 *
 * Locations are nested compound nodes (ТПЦ-7 → участки → оборудование). A parent
 * always wraps its children and auto-expands — children never overflow it.
 *
 * Devices show their device-type IMAGE (uploaded in the type), with a built-in
 * SVG icon fallback by type. Connection lines support bend points (waypoints):
 * select a line → drag the orange handles, double-click the line to add a bend,
 * right-click a handle to remove it. Bends persist per connection.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import dagre from 'cytoscape-dagre';
import {
  Box, Paper, Typography, IconButton, Tooltip, ToggleButton, ToggleButtonGroup,
  TextField, InputAdornment, Drawer, Divider, Chip, Stack, Button, Menu, MenuItem,
  FormControl, InputLabel, Select, OutlinedInput, Checkbox, ListItemText, CircularProgress,
  Alert, Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LabelIcon from '@mui/icons-material/Label';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import HubIcon from '@mui/icons-material/Hub';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PanToolIcon from '@mui/icons-material/PanTool';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MapIcon from '@mui/icons-material/Map';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import axios from '../api/axios';
import { ICON_FOR_TYPE } from '../components/deviceIcons';

try { cytoscape.use(fcose); } catch (e) { /* already */ }
try { cytoscape.use(dagre); } catch (e) { /* already */ }

const PALETTE = [
  '#2563eb', '#16a34a', '#db2777', '#ea580c', '#7c3aed', '#0891b2',
  '#ca8a04', '#dc2626', '#059669', '#4f46e5', '#c026d3', '#0d9488',
];
const colorForId = (id) => PALETTE[Math.abs(Number(id) || 0) % PALETTE.length];

const AUTO_LAYOUTS = {
  fcose: { name: 'fcose', quality: 'proof', animate: true, animationDuration: 600, randomize: true, fit: true, padding: 50, nodeSeparation: 110, idealEdgeLength: 130, nodeRepulsion: 8000, gravity: 0.2, gravityCompound: 1.2, nestingFactor: 0.1, packComponents: true },
  dagre: { name: 'dagre', rankDir: 'TB', nodeSep: 55, rankSep: 90, animate: true, animationDuration: 600, fit: true, padding: 50 },
  concentric: { name: 'concentric', animate: true, animationDuration: 600, fit: true, padding: 50, concentric: (n) => n.degree(), levelWidth: () => 2, minNodeSpacing: 60 },
};

const LS = { mode: 'mapauto.mode', group: 'mapauto.group', dark: 'mapauto.dark', minimap: 'mapauto.minimap' };

export default function MapAuto() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const cyRef = useRef(null);
  const saveTimer = useRef(null);

  const [topology, setTopology] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState(localStorage.getItem(LS.mode) || 'auto'); // auto | manual
  const [groupBy, setGroupBy] = useState(localStorage.getItem(LS.group) || 'location');
  const [autoLayout, setAutoLayout] = useState('fcose');
  const [dark, setDark] = useState(localStorage.getItem(LS.dark) === '1');
  const [showLabels, setShowLabels] = useState(true);
  const [locked, setLocked] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [hiddenTypes, setHiddenTypes] = useState([]);
  const [hiddenConnTypes, setHiddenConnTypes] = useState([]);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [toast, setToast] = useState(null);
  const [showMinimap, setShowMinimap] = useState(localStorage.getItem(LS.minimap) !== '0');
  const [cyVer, setCyVer] = useState(0); // bumps whenever a new cy instance is created (so MiniMap re-binds)

  useEffect(() => { localStorage.setItem(LS.mode, mode); }, [mode]);
  useEffect(() => { localStorage.setItem(LS.group, groupBy); }, [groupBy]);
  useEffect(() => { localStorage.setItem(LS.dark, dark ? '1' : '0'); }, [dark]);
  useEffect(() => { localStorage.setItem(LS.minimap, showMinimap ? '1' : '0'); }, [showMinimap]);

  const [searchParams] = useSearchParams();
  const rootDevice = searchParams.get('device');           // ?device=ID -> N-hop neighbourhood
  const rootHops = searchParams.get('hops') || '2';
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = rootDevice
        ? `/map/topology?root_device_id=${rootDevice}&max_hops=${rootHops}`
        : '/map/topology';
      const res = await axios.get(url);
      setTopology(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить карту');
    } finally { setLoading(false); }
  }, [rootDevice, rootHops]);
  useEffect(() => { load(); }, [load]);

  // saved positions: "device:ID" / "location:ID" -> {x,y}
  // Ignore non-finite or absurd coordinates (>20000) so corrupted/stale layouts
  // can't blow up the graph extent (which broke PNG export and made the map sparse).
  const savedPos = useMemo(() => {
    const m = {};
    (topology?.layouts || []).forEach((l) => {
      if (Number.isFinite(l.x) && Number.isFinite(l.y) && Math.abs(l.x) <= 20000 && Math.abs(l.y) <= 20000) {
        m[`${l.entity_type}:${l.entity_id}`] = { x: l.x, y: l.y, w: l.width, h: l.height };
      }
    });
    return m;
  }, [topology]);

  // ---- build elements ----
  const elements = useMemo(() => {
    if (!topology) return [];
    const els = [];
    const devices = topology.devices || [];
    const locations = topology.locations || [];
    const subnets = topology.subnets || [];
    const deviceTypes = topology.device_types || [];
    const dtName = Object.fromEntries(deviceTypes.map((t) => [t.id, t.name]));
    const usedParents = new Set();
    // saved manual zone sizes (width/height) keyed by location id
    const sizeOf = {};
    (topology.layouts || []).forEach((l) => { if (l.entity_type === 'location' && l.width && l.height) sizeOf[l.entity_id] = { w: l.width, h: l.height }; });

    const parentOf = (d) => {
      if (groupBy === 'location') return d.location_id ? `loc-${d.location_id}` : null;
      if (groupBy === 'subnet') { const s = (d.subnet_ids && d.subnet_ids[0]) || null; return s ? `subnet-${s}` : null; }
      if (groupBy === 'device_type') return d.device_type_id ? `dt-${d.device_type_id}` : null;
      return null;
    };
    devices.forEach((d) => { const p = parentOf(d); if (p) usedParents.add(p); });

    if (groupBy === 'location') {
      // Show ALL locations as nested zones (including empty ones, so the user
      // can place them and add devices later). A zone with no device-children
      // and no sub-zones is "empty" and gets a base size.
      const locIds = new Set(locations.map((l) => l.id));
      const hasDeviceChild = new Set(devices.filter((d) => d.location_id).map((d) => d.location_id));
      const hasLocChild = new Set(locations.filter((l) => l.parent_id).map((l) => l.parent_id));
      locations.forEach((l) => {
        const empty = !hasDeviceChild.has(l.id) && !hasLocChild.has(l.id);
        const sz = sizeOf[l.id];
        els.push({ data: {
          id: `loc-${l.id}`, label: l.name, kind: 'group', entityType: 'location', entityId: l.id,
          parent: (l.parent_id && locIds.has(l.parent_id)) ? `loc-${l.parent_id}` : undefined,
          fill: l.fill_color || '#ffffff', border: l.border_color || '#64748b', op: (l.opacity ?? 20) / 100,
          empty: empty ? 1 : 0,
          // manual zone size: saved value, else default (empty zones get a base box,
          // non-empty default to 1 = "auto" so the box just wraps its content).
          zoneW: (sz && sz.w) || (empty ? 180 : 1), zoneH: (sz && sz.h) || (empty ? 120 : 1),
        }});
      });
    } else if (groupBy === 'subnet') {
      subnets.forEach((s) => { const key = `subnet-${s.id}`; if (usedParents.has(key)) els.push({ data: { id: key, label: `${s.name}${s.vlan ? ' · VLAN ' + s.vlan : ''}`, kind: 'group', fill: '#f1f5f9', border: colorForId(s.id + 7), op: 0.18, zoneW: 1, zoneH: 1 } }); });
    } else if (groupBy === 'device_type') {
      deviceTypes.forEach((t) => { const key = `dt-${t.id}`; if (usedParents.has(key)) els.push({ data: { id: key, label: t.name, kind: 'group', fill: '#f8fafc', border: colorForId(t.id), op: 0.18, zoneW: 1, zoneH: 1 } }); });
    }

    devices.forEach((d) => {
      const userImg = d.image_path || d.device_type_image || null;
      const img = userImg
        ? (userImg.startsWith('http') || userImg.startsWith('/') ? userImg : `/${userImg}`)
        : ICON_FOR_TYPE(d.device_type_name || '');
      els.push({ data: {
        id: `dev-${d.id}`, deviceId: d.id, label: d.name,
        kind: 'device', parent: parentOf(d) || undefined,
        color: colorForId(d.device_type_id), image: img, hasUserImg: userImg ? 1 : 0,
        typeId: d.device_type_id, typeName: dtName[d.device_type_id] || '', ip: d.primary_ip || '', raw: d,
      }});
    });

    (topology.connections || []).forEach((c) => {
      const wps = Array.isArray(c.waypoints) ? c.waypoints : [];
      const seg = wpToSegments(wps);
      els.push({ data: {
        id: `conn-${c.id}`, connId: c.id, source: `dev-${c.source_device_id}`, target: `dev-${c.target_device_id}`,
        label: c.label || c.type_name || '', color: c.color || '#64748b',
        width: Math.max(1, Math.min(8, c.line_width || 2)),
        lineStyle: ({ solid: 'solid', dashed: 'dashed', dotted: 'dotted' }[c.line_style] || 'solid'),
        direction: c.direction || 'bidirectional', typeId: c.connection_type_id,
        wp: wps.length ? 1 : 0, segDist: seg.dist, segWeight: seg.weight, waypoints: wps,
        sEnd: handleToEndpoint(c.source_handle), tEnd: handleToEndpoint(c.target_handle), raw: c,
      }});
    });
    return els;
  }, [topology, groupBy]);

  const stylesheet = useMemo(() => buildStyle(dark, showLabels), [dark, showLabels]);

  // ---- create cytoscape ----
  useEffect(() => {
    if (!containerRef.current || loading) return;
    if (cyRef.current) { cyRef.current.destroy(); cyRef.current = null; }
    const cy = cytoscape({ container: containerRef.current, elements, style: stylesheet, wheelSensitivity: 3, minZoom: 0.05, maxZoom: 5 });
    cyRef.current = cy;
    if (typeof window !== 'undefined') window.__mapCy = cy; // debug aid for live testing
    setCyVer((v) => v + 1); // tell MiniMap a fresh cy instance exists

    cy.on('tap', 'node[kind="device"]', (e) => {
      const n = e.target;
      setSelected({ kind: 'device', data: n.data('raw') });
      cy.elements().removeClass('hl dim'); const nb = n.closedNeighborhood();
      cy.elements().difference(nb).addClass('dim'); nb.addClass('hl');
    });
    cy.on('tap', 'edge[kind!="wp"]', (e) => {
      selectEdge(cy, e.target);
      setSelected({ kind: 'connection', data: e.target.data('raw') });
    });
    cy.on('tap', (e) => {
      if (e.target === cy) { setSelected(null); cy.elements().removeClass('hl dim'); clearWaypointHandles(cy); }
    });

    // waypoint handle editing (any mode)
    cy.on('drag', 'node.wp-handle', (e) => updateWaypointFromHandle(cy, e.target));
    cy.on('dragfree', 'node.wp-handle', () => saveSelectedEdgeWaypoints(cy));
    cy.on('cxttap', 'node.wp-handle', (e) => removeWaypointHandle(cy, e.target));
    cy.on('position', 'node[kind="device"]', () => { if (selectedEdgeId(cy)) refreshWaypointHandles(cy); });

    // manual drag → persist node positions
    if (mode === 'manual') {
      cy.on('dragfree', 'node[kind="device"]', () => { scheduleSave(cy); syncZoneGrips(cy); });
      // dropping a zone onto another ejects it — zones must not overlap
      cy.on('dragfree', 'node[kind="group"]', () => { deOverlapZones(cy); syncZoneGrips(cy); scheduleSave(cy); });
      cy.on('drag', 'node[kind="group"]', () => syncZoneGrips(cy)); // grip follows a moving zone
      // always-visible resize grips: drag the blue corner to size the zone frame
      cy.on('drag', 'node.zone-resize', (e) => resizeZoneFromHandle(cy, e.target));
      cy.on('dragfree', 'node.zone-resize', () => { deOverlapZones(cy); syncZoneGrips(cy); scheduleSave(cy); });
      cy.on('layoutstop', () => syncZoneGrips(cy));
    }

    applyLayout(cy);
    return () => { cy.destroy(); cyRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, stylesheet, loading, mode]);

  const applyLayout = (cy) => {
    if (!cy) return;
    if (mode === 'manual') {
      // restore saved positions for devices AND zones (incl. empty zones)
      let anySaved = false;
      cy.nodes('[kind="device"]').forEach((n) => {
        const p = savedPos[`device:${n.data('deviceId')}`];
        if (p) { n.position(p); anySaved = true; }
      });
      cy.nodes('[kind="group"]').forEach((n) => {
        if (n.data('entityType') !== 'location') return;
        const p = savedPos[`location:${n.data('entityId')}`];
        if (p) { n.position(p); anySaved = true; }
      });
      // self-heal: if saved positions produce an insane spread (corrupt/stale
      // layout), discard them and auto-arrange instead of rendering a broken map.
      if (anySaved) {
        const bb = cy.nodes().boundingBox();
        // Corrupt save = absurdly spread (>15000) OR collapsed: many devices
        // crammed into a tiny area (a degenerate save that renders as one
        // unreadable blob in a corner). Either way, discard -> auto-arrange.
        const dev = cy.nodes('[kind="device"]');
        const dbb = dev.boundingBox();
        const collapsed = dev.length >= 8 && isFinite(dbb.w) && (dbb.w * dbb.h) / dev.length < 4000;
        if (!isFinite(bb.w) || bb.w > 15000 || bb.h > 15000 || collapsed) {
          anySaved = false;
          setToast('Сохранённая расстановка была повреждена — разложил автоматически');
        }
      }
      if (anySaved) {
        cy.layout({ name: 'preset', fit: true, padding: 50, positions: (n) => n.position() }).run();
        // self-heal saved overlaps: if zones in the stored layout sit on top of
        // each other, separate them and persist the corrected positions.
        if (deOverlapZones(cy)) { scheduleSave(cy, true); setToast('Участки разведены, чтобы не накладывались'); }
        cy.fit(undefined, 50);
        syncZoneGrips(cy); // preset layout doesn't fire layoutstop — create grips explicitly
      } else {
        const l = cy.elements(':visible').layout(AUTO_LAYOUTS.fcose);
        l.one('layoutstop', () => { deOverlapZones(cy); scheduleSave(cy, true); syncZoneGrips(cy); });
        l.run();
      }
    } else {
      const vis = cy.elements(':visible');
      const l = (vis.length ? vis : cy.elements()).layout(AUTO_LAYOUTS[autoLayout] || AUTO_LAYOUTS.fcose);
      l.one('layoutstop', () => { if (deOverlapZones(cy)) cy.fit(undefined, 50); });
      l.run();
    }
  };

  const scheduleSave = (cy, immediate = false) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const doSave = async () => {
      const payload = [];
      cy.nodes('[kind="device"]').forEach((n) => {
        const p = n.position();
        payload.push({ entity_type: 'device', entity_id: n.data('deviceId'), x: p.x, y: p.y });
      });
      // persist zone (location) positions too — matters for empty zones and
      // so another user sees the same layout next week
      cy.nodes('[kind="group"]').forEach((n) => {
        if (n.data('entityType') !== 'location') return;
        const p = n.position();
        // persist the manual zone size too (only if it's an explicit size, not "auto")
        const w = n.data('zoneW'), h = n.data('zoneH');
        const entry = { entity_type: 'location', entity_id: n.data('entityId'), x: p.x, y: p.y };
        if (w > 1 && h > 1) { entry.width = w; entry.height = h; }
        payload.push(entry);
      });
      if (!payload.length) return;
      try { await axios.post('/map-layouts/bulk-save', payload); setToast('Сохранено'); }
      catch (e) { setToast('Не удалось сохранить позиции'); }
    };
    if (immediate) doSave(); else saveTimer.current = setTimeout(doSave, 700);
  };

  // ---- lock: elements ignore the mouse → dragging anywhere just pans the map ----
  useEffect(() => {
    const cy = cyRef.current; if (!cy) return;
    cy.batch(() => cy.elements().toggleClass('noevents', locked));
  }, [locked, elements]);

  // ---- always-visible zone resize grips (manual mode) ----
  // Done in a deferred effect (not inside applyLayout) because the preset layout
  // hasn't committed positions yet when applyLayout returns, so grips created
  // there land at non-finite corners and get dropped. A short delay lets the
  // layout settle, then grips appear at the right corners.
  useEffect(() => {
    const cy = cyRef.current; if (!cy) return undefined;
    if (mode !== 'manual') { clearZoneGrips(cy); return undefined; }
    const t = setTimeout(() => { try { syncZoneGrips(cy); } catch (e) { /* noop */ } }, 350);
    return () => clearTimeout(t);
  }, [cyVer, mode, elements]);

  // ---- filters ----
  useEffect(() => {
    const cy = cyRef.current; if (!cy) return;
    cy.batch(() => {
      cy.nodes('[kind="device"]').forEach((n) => n.toggleClass('hidden', hiddenTypes.includes(n.data('typeId'))));
      cy.edges('[kind!="wp"]').forEach((e) => e.toggleClass('hidden',
        hiddenConnTypes.includes(e.data('typeId')) || e.source().hasClass('hidden') || e.target().hasClass('hidden')));
    });
  }, [hiddenTypes, hiddenConnTypes, elements]);

  // ---- search ----
  useEffect(() => {
    const cy = cyRef.current; if (!cy) return;
    const q = search.trim().toLowerCase();
    cy.elements().removeClass('hl dim');
    if (!q) return;
    const m = cy.nodes('[kind="device"]').filter((n) => {
      const d = n.data('raw');
      return (d.name || '').toLowerCase().includes(q) || (d.primary_ip || '').toLowerCase().includes(q) || (d.device_type_name || '').toLowerCase().includes(q);
    });
    if (m.length) { cy.elements().addClass('dim'); m.removeClass('dim').addClass('hl'); cy.animate({ fit: { eles: m, padding: 80 }, duration: 400 }); }
  }, [search]);

  // ---- controls ----
  const fit = () => cyRef.current && cyRef.current.animate({ fit: { padding: 50 }, duration: 300 });
  const zoomBy = (f) => { const cy = cyRef.current; if (cy) cy.animate({ zoom: cy.zoom() * f, center: { eles: cy.elements(':visible') } }, { duration: 200 }); };
  const rerun = () => applyLayout(cyRef.current);
  const resetToAuto = () => {
    const cy = cyRef.current; if (!cy) return;
    const l = cy.elements(':visible').layout(AUTO_LAYOUTS.fcose);
    l.one('layoutstop', () => { deOverlapZones(cy); if (mode === 'manual') scheduleSave(cy, true); });
    l.run();
    setToast('Переразложено автоматически' + (mode === 'manual' ? ' и сохранено' : ''));
  };
  // separate any overlapping zones on demand (manual mode "tidy floor plan")
  const tidyZones = () => {
    const cy = cyRef.current; if (!cy) return;
    if (deOverlapZones(cy)) {
      if (mode === 'manual') scheduleSave(cy, true);
      cy.animate({ fit: { padding: 50 }, duration: 300 });
      setToast('Участки разведены без нахлёста');
    } else setToast('Нахлёстов участков нет');
  };
  const exportPng = (scale) => {
    const cy = cyRef.current; setExportAnchor(null); if (!cy) return;
    try {
      // blob output + safe scale computed from the actual graph extent: a huge
      // extent × scale overflows the browser canvas limit → 0 KB file. Cap the
      // exported canvas to ~6000px on the longest side.
      const bb = cy.elements(':visible').boundingBox();
      const cap = 6000;
      const safeScale = Math.max(0.2, Math.min(scale, cap / Math.max(1, bb.w), cap / Math.max(1, bb.h)));
      const grips = cy.nodes('.zone-resize'); grips.style('display', 'none'); // keep resize grips out of the export
      let blob;
      try { blob = cy.png({ output: 'blob', full: true, scale: safeScale, bg: dark ? '#0f172a' : '#ffffff' }); }
      finally { grips.removeStyle('display'); }
      if (!blob || !blob.size) { setToast('Не удалось создать PNG (пустой)'); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `network-map-${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setToast(`PNG сохранён (${Math.round(blob.size / 1024)} КБ)`);
    } catch (e) {
      setToast('Ошибка экспорта PNG');
    }
  };

  // add a bend point to the currently-selected connection (from its side panel)
  const addBendToSelected = () => {
    const cy = cyRef.current; if (!cy || selected?.kind !== 'connection') return;
    const edge = cy.getElementById(`conn-${selected.data.id}`); if (edge.empty()) return;
    const wps = (edge.data('waypoints') || []).concat([{ w: 0.5, d: 0 }]).sort((a, b) => a.w - b.w);
    edge.data('waypoints', wps); applyEdgeSegments(edge, wps);
    clearWaypointHandles(cy); cy.scratch('_selEdge', edge.id()); refreshWaypointHandles(cy);
    saveSelectedEdgeWaypoints(cy);
    setToast('Точка сгиба добавлена — тащи оранжевый кружок');
  };
  const straightenSelected = () => {
    const cy = cyRef.current; if (!cy || selected?.kind !== 'connection') return;
    const edge = cy.getElementById(`conn-${selected.data.id}`); if (edge.empty()) return;
    edge.data('waypoints', []); applyEdgeSegments(edge, []); clearWaypointHandles(cy);
    axios.put(`/map/connection/${edge.data('connId')}/waypoints`, { waypoints: [] }).catch(() => {});
    setToast('Линия выпрямлена');
  };
  // attach a connection's end to a chosen perimeter point of its device.
  // code: 'N'|'NE'|… or '' for auto (nearest point). '' is the reset sentinel —
  // the PUT endpoint ignores nulls, so we store '' which maps back to auto.
  const setAnchor = (side, code) => {
    const cy = cyRef.current; if (!cy || selected?.kind !== 'connection') return;
    const edge = cy.getElementById(`conn-${selected.data.id}`); if (edge.empty()) return;
    const field = side === 'source' ? 'source_handle' : 'target_handle';
    edge.data(side === 'source' ? 'sEnd' : 'tEnd', handleToEndpoint(code));
    edge.data('raw', { ...edge.data('raw'), [field]: code || '' });
    axios.put(`/connections/${edge.data('connId')}`, { [field]: code || '' }).catch(() => {});
    setSelected((s) => (s && s.kind === 'connection') ? { ...s, data: { ...s.data, [field]: code || '' } } : s);
    if (selectedEdgeId(cy)) refreshWaypointHandles(cy);
    setToast(side === 'source' ? 'Точка источника изменена' : 'Точка приёмника изменена');
  };

  const deviceTypes = topology?.device_types || [];
  const connTypes = topology?.connection_types || [];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={load}>Повторить</Button>}>{error}</Alert></Box>;
  const stats = topology?.stats || {};

  return (
    <Box sx={{ height: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
      <Paper elevation={1} sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <HubIcon color="primary" />
        <Typography variant="h6" sx={{ mr: 1 }}>Карта сети</Typography>

        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setMode(v)}>
          <ToggleButton value="auto"><AutoFixHighIcon fontSize="small" sx={{ mr: 0.5 }} />Авто</ToggleButton>
          <ToggleButton value="manual"><PanToolIcon fontSize="small" sx={{ mr: 0.5 }} />Моя расстановка</ToggleButton>
        </ToggleButtonGroup>

        <TextField size="small" placeholder="Поиск…" value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ width: 180 }} />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Группировать</InputLabel>
          <Select value={groupBy} label="Группировать" onChange={(e) => setGroupBy(e.target.value)}>
            <MenuItem value="location">Локации (вложенные)</MenuItem>
            <MenuItem value="subnet">Подсети</MenuItem>
            <MenuItem value="device_type">Типу устройств</MenuItem>
            <MenuItem value="none">Без групп</MenuItem>
          </Select>
        </FormControl>

        {mode === 'auto' && (
          <ToggleButtonGroup size="small" exclusive value={autoLayout} onChange={(e, v) => { if (v) { setAutoLayout(v); setTimeout(() => applyLayout(cyRef.current), 0); } }}>
            <ToggleButton value="fcose">Силовая</ToggleButton>
            <ToggleButton value="dagre">Иерархия</ToggleButton>
            <ToggleButton value="concentric">Кольца</ToggleButton>
          </ToggleButtonGroup>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Типы устройств</InputLabel>
          <Select multiple value={deviceTypes.filter((t) => !hiddenTypes.includes(t.id)).map((t) => t.id)}
            input={<OutlinedInput label="Типы устройств" />} renderValue={(s) => `${s.length}/${deviceTypes.length}`}
            onChange={(e) => { const shown = e.target.value; setHiddenTypes(deviceTypes.filter((t) => !shown.includes(t.id)).map((t) => t.id)); }}>
            {deviceTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                <Checkbox checked={!hiddenTypes.includes(t.id)} size="small" />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: colorForId(t.id), mr: 1 }} />
                <ListItemText primary={t.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title={locked ? 'Разблокировать (двигать элементы)' : 'Заблокировать (двигать только карту)'}>
          <IconButton onClick={() => setLocked((v) => !v)} color={locked ? 'primary' : 'default'}>{locked ? <LockIcon /> : <LockOpenIcon />}</IconButton>
        </Tooltip>
        {mode === 'manual' && <Tooltip title="Разложить автоматически и запомнить"><IconButton onClick={resetToAuto}><AutoFixHighIcon /></IconButton></Tooltip>}
        {mode === 'manual' && <Tooltip title="Развести участки, чтобы не накладывались"><IconButton onClick={tidyZones}><ViewModuleIcon /></IconButton></Tooltip>}
        <Tooltip title="Подогнать"><IconButton onClick={fit}><CenterFocusStrongIcon /></IconButton></Tooltip>
        <Tooltip title="Приблизить"><IconButton onClick={() => zoomBy(1.3)}><ZoomInIcon /></IconButton></Tooltip>
        <Tooltip title="Отдалить"><IconButton onClick={() => zoomBy(0.77)}><ZoomOutIcon /></IconButton></Tooltip>
        {mode === 'auto' && <Tooltip title="Перестроить"><IconButton onClick={rerun}><RefreshIcon /></IconButton></Tooltip>}
        <Tooltip title={showLabels ? 'Скрыть подписи' : 'Показать подписи'}><IconButton onClick={() => setShowLabels((v) => !v)}>{showLabels ? <LabelIcon /> : <LabelOffIcon />}</IconButton></Tooltip>
        <Tooltip title={dark ? 'Светлая' : 'Тёмная'}><IconButton onClick={() => setDark((v) => !v)}>{dark ? <LightModeIcon /> : <DarkModeIcon />}</IconButton></Tooltip>
        <Tooltip title={showMinimap ? 'Скрыть мини-карту' : 'Показать мини-карту'}><IconButton onClick={() => setShowMinimap((v) => !v)} color={showMinimap ? 'primary' : 'default'}><MapIcon /></IconButton></Tooltip>
        <Tooltip title="Экспорт PNG"><IconButton onClick={(e) => setExportAnchor(e.currentTarget)}><DownloadIcon /></IconButton></Tooltip>
        <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
          <MenuItem onClick={() => exportPng(1.5)}>PNG (обычный)</MenuItem>
          <MenuItem onClick={() => exportPng(3)}>PNG (высокое качество)</MenuItem>
        </Menu>
      </Paper>

      <Box sx={{ position: 'relative', flexGrow: 1, minHeight: 0 }}>
        <Box ref={containerRef} sx={{ position: 'absolute', inset: 0, bgcolor: dark ? '#0f172a' : '#f8fafc', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
        <Paper sx={{ position: 'absolute', top: 8, left: 8, px: 1.5, py: 0.5, opacity: 0.95 }}>
          <Typography variant="caption">{stats.devices || 0} устройств · {stats.connections || 0} связей · {stats.locations || 0} локаций{mode === 'manual' ? ' · ручной режим' : ''}</Typography>
        </Paper>
        {rootDevice && (
          <Paper sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', px: 1.5, py: 0.25, opacity: 0.96, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption">Окрестность устройства (#{rootDevice}, {rootHops} ур.)</Typography>
            <Button size="small" onClick={() => navigate('/map-auto')}>Вся карта</Button>
          </Paper>
        )}
        {mode === 'manual' && (
          <Paper sx={{ position: 'absolute', top: 8, right: 8, px: 1.5, py: 0.5, opacity: 0.92, maxWidth: 280 }}>
            <Typography variant="caption" color="text.secondary">
              Тяни устройства и участки. У каждого участка в углу синий квадрат — тяни его, чтобы менять размер. Клик по линии → панель справа (там сгибы и точки подключения).
            </Typography>
          </Paper>
        )}
        {connTypes.length > 0 && (
          <Paper sx={{ position: 'absolute', bottom: 8, left: 8, p: 1, maxWidth: 240, opacity: 0.96 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Типы связей</Typography>
            <Stack spacing={0.3} sx={{ mt: 0.5 }}>
              {connTypes.map((ct) => (
                <Box key={ct.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'pointer', opacity: hiddenConnTypes.includes(ct.id) ? 0.4 : 1 }}
                  onClick={() => setHiddenConnTypes((p) => p.includes(ct.id) ? p.filter((x) => x !== ct.id) : [...p, ct.id])}>
                  <Box sx={{ width: 22, borderTop: `${Math.max(2, ct.line_width)}px ${ct.line_style} ${ct.color}` }} />
                  <Typography variant="caption">{ct.name}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}
        <Paper sx={{ position: 'absolute', bottom: showMinimap ? 156 : 8, right: 8, px: 1.5, py: 0.5, opacity: 0.85 }}>
          <Typography variant="caption" color="text.secondary">тяни пустое место — двигать · колесо — масштаб · ⊕ подогнать всё</Typography>
        </Paper>
        <MiniMap cyRef={cyRef} cyVer={cyVer} dark={dark} visible={showMinimap} />

        {(topology?.devices || []).length === 0 && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <HubIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography color="text.secondary">Пока нет устройств</Typography>
            <Button variant="outlined" onClick={() => navigate('/devices')}>Добавить устройства</Button>
          </Box>
        )}
      </Box>

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: 320, p: 2, pt: 9 } }}>
        {selected?.kind === 'device' && <DevicePanel d={selected.data} onClose={() => setSelected(null)} onOpen={() => navigate(`/devices/${selected.data.id}`)} />}
        {selected?.kind === 'connection' && <ConnectionPanel c={selected.data} onClose={() => setSelected(null)} onAddBend={addBendToSelected} onStraighten={straightenSelected} onSetAnchor={setAnchor} />}
      </Drawer>

      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast(null)} message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ───────────────────────── anchor helpers ─────────────────────────
// 16 perimeter anchor points: a connection end attaches at "<x>% <y>%" relative
// to the device centre (±50% of its size). The handle is now stored as that
// endpoint string directly. 4 corners + 3 points per side = 16 points; the
// 5×5 picker's outer ring maps 1:1 to them, centre = auto.
const ANCHOR_PCT = [-50, -25, 0, 25, 50];
// grid cell (row, col) → endpoint string if on the perimeter, else null (auto)
function anchorCell(r, c) {
  if (r === 0 || r === 4 || c === 0 || c === 4) return `${ANCHOR_PCT[c]}% ${ANCHOR_PCT[r]}%`;
  return null;
}
// legacy 8-code map (older saved handles) → endpoint string
const LEGACY_EP = {
  N: '0% -50%', NE: '50% -50%', E: '50% 0%', SE: '50% 50%',
  S: '0% 50%', SW: '-50% 50%', W: '-50% 0%', NW: '-50% -50%',
};
function handleToEndpoint(v) {
  if (!v) return 'outside-to-node';
  if (typeof v === 'string' && v.indexOf('%') !== -1) return v; // already an endpoint string
  return LEGACY_EP[v] || 'outside-to-node';
}

// ───────────────────────── waypoint helpers ─────────────────────────
// waypoints stored as [{d, w}]: d = perpendicular px offset, w = position 0..1
function wpToSegments(wps) {
  if (!wps || !wps.length) return { dist: '0', weight: '0.5' };
  const sorted = [...wps].sort((a, b) => a.w - b.w);
  return { dist: sorted.map((p) => p.d).join(' '), weight: sorted.map((p) => p.w).join(' ') };
}
function segPointToAbs(S, T, d, w) {
  const vx = T.x - S.x, vy = T.y - S.y;
  const len = Math.hypot(vx, vy) || 1;
  const bx = S.x + vx * w, by = S.y + vy * w;
  const nx = -vy / len, ny = vx / len;
  return { x: bx + nx * d, y: by + ny * d };
}
function absToSeg(S, T, P) {
  const vx = T.x - S.x, vy = T.y - S.y;
  const len2 = vx * vx + vy * vy || 1;
  const w = Math.max(-0.4, Math.min(1.4, ((P.x - S.x) * vx + (P.y - S.y) * vy) / len2));
  const len = Math.sqrt(len2);
  const bx = S.x + vx * w, by = S.y + vy * w;
  const nx = -vy / len, ny = vx / len;
  const d = (P.x - bx) * nx + (P.y - by) * ny;
  return { d, w };
}
function selectEdge(cy, edge) {
  clearWaypointHandles(cy);
  selectedEdgeRef_set(cy, edge.id());
  refreshWaypointHandles(cy);
}
// store selected edge id on the cy instance (avoids stale closure)
function selectedEdgeRef_set(cy, id) { cy.scratch('_selEdge', id); }
function selectedEdgeId(cy) { return cy.scratch('_selEdge'); }
function clearWaypointHandles(cy) {
  cy.remove(cy.nodes('.wp-handle'));
  cy.scratch('_selEdge', null);
}
function refreshWaypointHandles(cy) {
  const id = selectedEdgeId(cy); if (!id) return;
  const edge = cy.getElementById(id); if (!edge || edge.empty()) return;
  cy.remove(cy.nodes('.wp-handle'));
  const wps = (edge.data('waypoints') || []).slice().sort((a, b) => a.w - b.w);
  if (!wps.length) return;
  const S = edge.source().position(), T = edge.target().position();
  // Place handles exactly on the rendered bend vertices when available, so they
  // never drift off the line; fall back to computed positions otherwise.
  let pts = [];
  try { pts = edge.segmentPoints() || []; } catch (e) { pts = []; }
  wps.forEach((p, i) => {
    const rp = pts[i];
    const abs = (rp && isFinite(rp.x) && isFinite(rp.y)) ? { x: rp.x, y: rp.y } : segPointToAbs(S, T, p.d, p.w);
    cy.add({ group: 'nodes', data: { id: `wp-${edge.data('connId')}-${i}`, kind: 'wp', forEdge: id, idx: i }, position: abs, classes: 'wp-handle' });
  });
}
function updateWaypointFromHandle(cy, handle) {
  const edge = cy.getElementById(handle.data('forEdge')); if (edge.empty()) return;
  const idx = handle.data('idx');
  const S = edge.source().position(), T = edge.target().position();
  const sorted = (edge.data('waypoints') || []).slice().sort((a, b) => a.w - b.w);
  if (!sorted[idx]) return;
  const { d, w } = absToSeg(S, T, handle.position());
  sorted[idx] = { d, w };
  edge.data('waypoints', sorted);
  applyEdgeSegments(edge, sorted);
}
function removeWaypointHandle(cy, handle) {
  const edge = cy.getElementById(handle.data('forEdge')); if (edge.empty()) return;
  const idx = handle.data('idx');
  const sorted = (edge.data('waypoints') || []).slice().sort((a, b) => a.w - b.w);
  sorted.splice(idx, 1);
  edge.data('waypoints', sorted);
  applyEdgeSegments(edge, sorted);
  refreshWaypointHandles(cy);
  saveSelectedEdgeWaypoints(cy);
}
function applyEdgeSegments(edge, wps) {
  const seg = wpToSegments(wps);
  edge.data('wp', wps.length ? 1 : 0);
  edge.data('segDist', seg.dist);
  edge.data('segWeight', seg.weight);
}
async function saveSelectedEdgeWaypoints(cy) {
  const id = selectedEdgeId(cy); if (!id) return;
  const edge = cy.getElementById(id); if (edge.empty()) return;
  const wps = edge.data('waypoints') || [];
  try { await axios.put(`/map/connection/${edge.data('connId')}/waypoints`, { waypoints: wps }); }
  catch (e) { /* non-fatal */ }
}

// ───────────────────────── zone de-overlap ─────────────────────────
// A zone is a "physical area" on the floor plan: two zones must never sit on
// top of each other (unless one is nested inside the other). We move a zone by
// shifting its descendant devices — the compound box follows automatically
// (verified: moving the leaves moves the parent, no double-move).
function zonesShift(g, dx, dy) {
  const dev = g.descendants().filter('[kind="device"]');
  if (dev.nonempty()) dev.shift({ x: dx, y: dy }); else g.shift({ x: dx, y: dy });
}
function deOverlapZones(cy) {
  const zs = cy.nodes('[kind="group"]');
  if (zs.length < 2) return 0;
  const GAP = 24; // minimum breathing room between two zones
  let moved = 0;
  for (let it = 0; it < 80; it++) {
    let any = false;
    for (let i = 0; i < zs.length; i++) for (let j = i + 1; j < zs.length; j++) {
      const a = zs[i], b = zs[j];
      // nested zones SHOULD overlap (child inside parent) — skip those pairs
      if (a.ancestors().contains(b) || b.ancestors().contains(a)) continue;
      const ba = a.boundingBox(), bb = b.boundingBox();
      const ox = Math.min(ba.x2, bb.x2) - Math.max(ba.x1, bb.x1) + GAP;
      const oy = Math.min(ba.y2, bb.y2) - Math.max(ba.y1, bb.y1) + GAP;
      if (ox > 0 && oy > 0) {
        any = true; moved++;
        const cax = (ba.x1 + ba.x2) / 2, cbx = (bb.x1 + bb.x2) / 2;
        const cay = (ba.y1 + ba.y2) / 2, cby = (bb.y1 + bb.y2) / 2;
        if (ox < oy) { // push apart along the axis of least penetration
          const d = cax <= cbx ? -1 : 1, s = ox / 2;
          zonesShift(a, d * s, 0); zonesShift(b, -d * s, 0);
        } else {
          const d = cay <= cby ? -1 : 1, s = oy / 2;
          zonesShift(a, 0, d * s); zonesShift(b, 0, -d * s);
        }
      }
    }
    if (!any) break;
  }
  return moved;
}

// ───────────────────────── zone resize ─────────────────────────
// A zone (compound) is sized via its min-width/min-height data (zoneW/zoneH).
// Every zone shows an ALWAYS-VISIBLE blue grip at its bottom-right corner (manual
// mode) — no clicking-to-select needed (clicks there usually hit a device). Drag
// the grip to size the frame; it grows symmetrically around the zone centre, so
// the devices inside stay put while the frame grows/shrinks around them.
function syncZoneGrips(cy) {
  const zones = cy.nodes('[kind="group"]');
  const wanted = new Set(zones.map((z) => `zrh-${z.id()}`));
  cy.nodes('.zone-resize').forEach((g) => { if (!wanted.has(g.id())) cy.remove(g); }); // drop stale
  zones.forEach((z) => {
    const bb = z.boundingBox();
    const id = `zrh-${z.id()}`;
    const g = cy.getElementById(id);
    if (g.empty()) cy.add({ group: 'nodes', data: { id, kind: 'zrh', forZone: z.id() }, position: { x: bb.x2, y: bb.y2 }, classes: 'zone-resize' });
    else if (!g.grabbed()) g.position({ x: bb.x2, y: bb.y2 }); // don't fight the one being dragged
  });
}
function clearZoneGrips(cy) { cy.remove(cy.nodes('.zone-resize')); }
function resizeZoneFromHandle(cy, handle) {
  const z = cy.getElementById(handle.data('forZone')); if (z.empty()) return;
  const c = z.position(); const hp = handle.position(); const MIN = 70;
  z.data('zoneW', Math.max(MIN, (hp.x - c.x) * 2));
  z.data('zoneH', Math.max(MIN, (hp.y - c.y) * 2));
}

// ───────────────────────── style ─────────────────────────
function buildStyle(dark, showLabels) {
  const text = dark ? '#e2e8f0' : '#0f172a';
  const sub = dark ? '#94a3b8' : '#64748b';
  return [
    { selector: 'node[kind="device"]', style: {
      'background-image': 'data(image)', 'background-clip': 'node',
      'width': 50, 'height': 50, 'shape': 'round-rectangle',
      'label': showLabels ? 'data(label)' : '', 'font-size': 11, 'color': text,
      'text-valign': 'bottom', 'text-margin-y': 4, 'text-wrap': 'wrap', 'text-max-width': 110,
      'text-background-color': dark ? '#0f172a' : '#ffffff', 'text-background-opacity': showLabels ? 0.7 : 0,
      'text-background-padding': 2, 'text-background-shape': 'round-rectangle',
    }},
    // built-in icon: coloured tile, white glyph centred with padding (fixed size, never off-centre)
    { selector: 'node[kind="device"][hasUserImg=0]', style: {
      'background-color': 'data(color)', 'background-opacity': 1,
      'background-fit': 'none', 'background-width': '58%', 'background-height': '58%',
      'background-position-x': '50%', 'background-position-y': '50%',
      'border-width': 2, 'border-color': dark ? '#1e293b' : '#ffffff',
    }},
    // user-uploaded image: white tile, image contained with small inset, light border (no dark letterbox)
    { selector: 'node[kind="device"][hasUserImg=1]', style: {
      'background-color': dark ? '#1e293b' : '#ffffff', 'background-opacity': 1,
      'background-fit': 'contain', 'background-image-containment': 'inside',
      'background-width': '80%', 'background-height': '80%', // ~5px inner padding on a 50px tile
      'background-position-x': '50%', 'background-position-y': '50%',
      'border-width': 1, 'border-color': dark ? '#334155' : '#e2e8f0',
    }},
    { selector: 'node[kind="group"]', style: {
      'background-color': 'data(fill)', 'background-opacity': 'data(op)',
      'border-width': 2, 'border-color': 'data(border)', 'shape': 'round-rectangle',
      'label': 'data(label)', 'font-size': 14, 'font-weight': 'bold', 'color': sub,
      'text-valign': 'top', 'text-halign': 'center', 'text-margin-y': -4, 'padding': 30, 'corner-radius': 14,
      // manual zone size: min-width/height come from data so a sparse zone can be
      // drawn big (and a packed one stays compact). zoneW/zoneH = 1 means "auto".
      'min-width': 'data(zoneW)', 'min-height': 'data(zoneH)',
    }},
    // empty zones (no children) get an explicit, resizable size box
    { selector: 'node[kind="group"][empty=1]', style: { 'width': 'data(zoneW)', 'height': 'data(zoneH)', 'border-style': 'dashed' } },
    { selector: 'node.wp-handle', style: {
      'width': 12, 'height': 12, 'shape': 'ellipse', 'background-color': '#f59e0b',
      'border-width': 2, 'border-color': '#fff', 'label': '', 'z-index': 9999,
    }},
    // zone resize grip (bottom-right corner of a selected zone)
    { selector: 'node.zone-resize', style: {
      'width': 15, 'height': 15, 'shape': 'round-rectangle', 'background-color': '#2563eb',
      'border-width': 2, 'border-color': '#fff', 'label': '', 'z-index': 9999,
    }},
    { selector: 'edge', style: {
      'width': 'data(width)', 'line-color': 'data(color)', 'line-style': 'data(lineStyle)',
      'curve-style': 'bezier', 'target-arrow-color': 'data(color)',
      'source-endpoint': 'data(sEnd)', 'target-endpoint': 'data(tEnd)',
      'label': showLabels ? 'data(label)' : '', 'font-size': 9, 'color': sub,
      'text-rotation': 'autorotate', 'text-background-color': dark ? '#0f172a' : '#ffffff',
      'text-background-opacity': 0.7, 'text-background-padding': 2, 'opacity': 0.9,
    }},
    { selector: 'edge[wp=1]', style: {
      'curve-style': 'segments', 'segment-distances': 'data(segDist)', 'segment-weights': 'data(segWeight)',
    }},
    { selector: 'edge[direction="source_to_target"]', style: { 'target-arrow-shape': 'triangle' } },
    { selector: 'edge[direction="target_to_source"]', style: { 'source-arrow-shape': 'triangle', 'source-arrow-color': 'data(color)' } },
    { selector: 'node:selected', style: { 'border-width': 4, 'border-color': '#f59e0b' } },
    { selector: 'edge:selected', style: { 'width': 5, 'opacity': 1, 'line-color': '#f59e0b', 'target-arrow-color': '#f59e0b' } },
    { selector: '.dim', style: { 'opacity': 0.12 } },
    { selector: '.hl', style: { 'opacity': 1 } },
    { selector: '.hidden', style: { 'display': 'none' } },
    { selector: '.noevents', style: { 'events': 'no' } },
  ];
}

// Lightweight DIY minimap (no cytoscape-navigator/jQuery): draws zone rects +
// device dots + a viewport rectangle on a small canvas. Click/drag to pan.
function MiniMap({ cyRef, cyVer, dark, visible }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const cy = cyRef.current; const canvas = canvasRef.current;
    if (!cy || !canvas || !visible) return undefined;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height, pad = 6;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = dark ? '#0f172a' : '#f1f5f9';
      ctx.fillRect(0, 0, W, H);
      const bb = cy.elements(':visible').boundingBox();
      if (!bb || !isFinite(bb.w) || bb.w <= 0 || bb.h <= 0) { canvas._mini = null; return; }
      const s = Math.min((W - 2 * pad) / bb.w, (H - 2 * pad) / bb.h);
      const ox = pad + (W - 2 * pad - bb.w * s) / 2 - bb.x1 * s;
      const oy = pad + (H - 2 * pad - bb.h * s) / 2 - bb.y1 * s;
      const mx = (x) => ox + x * s, my = (y) => oy + y * s;
      cy.nodes('[kind="group"]:visible').forEach((n) => {
        const b = n.boundingBox();
        ctx.strokeStyle = dark ? '#475569' : '#cbd5e1'; ctx.lineWidth = 1;
        ctx.strokeRect(mx(b.x1), my(b.y1), b.w * s, b.h * s);
      });
      cy.nodes('[kind="device"]:visible').forEach((n) => {
        const p = n.position();
        ctx.fillStyle = n.data('color') || '#64748b';
        ctx.fillRect(mx(p.x) - 1.5, my(p.y) - 1.5, 3, 3);
      });
      const ext = cy.extent();
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 1.5;
      ctx.strokeRect(mx(ext.x1), my(ext.y1), (ext.x2 - ext.x1) * s, (ext.y2 - ext.y1) * s);
      canvas._mini = { ox, oy, s };
    };
    let raf = null;
    const sched = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = null; draw(); }); };
    draw();
    cy.on('render', sched);
    const panTo = (clientX, clientY) => {
      const m = canvas._mini; if (!m) return;
      const r = canvas.getBoundingClientRect();
      const modelX = (clientX - r.left - m.ox) / m.s;
      const modelY = (clientY - r.top - m.oy) / m.s;
      const z = cy.zoom();
      cy.pan({ x: cy.width() / 2 - modelX * z, y: cy.height() / 2 - modelY * z });
    };
    let dragging = false;
    const onDown = (e) => { dragging = true; panTo(e.clientX, e.clientY); e.preventDefault(); };
    const onMove = (e) => { if (dragging) panTo(e.clientX, e.clientY); };
    const onUp = () => { dragging = false; };
    canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      cy.removeListener('render', sched);
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [cyRef, cyVer, dark, visible]);
  if (!visible) return null;
  return (
    <Paper sx={{ position: 'absolute', bottom: 8, right: 8, p: 0.5, lineHeight: 0, opacity: 0.96 }}>
      <canvas ref={canvasRef} width={200} height={140} style={{ display: 'block', borderRadius: 4, cursor: 'crosshair' }} />
    </Paper>
  );
}
function DevicePanel({ d, onClose, onOpen }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{d.name}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Chip size="small" label={d.device_type_name || 'без типа'} sx={{ mt: 0.5 }} />
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={1}>
        <Row label="IP" value={d.ip_addresses?.length ? d.ip_addresses.join(', ') : '—'} />
        <Row label="Интерфейсов" value={d.interface_count ?? 0} />
        <Row label="Подсетей" value={d.subnet_ids?.length ?? 0} />
        {d.description && <Row label="Описание" value={d.description} />}
      </Stack>
      <Button fullWidth variant="contained" startIcon={<OpenInNewIcon />} sx={{ mt: 2 }} onClick={onOpen}>Открыть карточку</Button>
    </Box>
  );
}
// 5×5 picker: outer ring = 16 perimeter anchor points, centre = auto.
function AnchorCompass({ title, value, onPick }) {
  const cur = value || '';
  const rows = [0, 1, 2, 3, 4];
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 0.5 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 18px)', gridTemplateRows: 'repeat(5, 18px)', gap: '3px' }}>
        {rows.flatMap((r) => rows.map((c) => {
          const ep = anchorCell(r, c);
          const isCenter = r === 2 && c === 2;
          if (ep === null && !isCenter) return <Box key={`${r}-${c}`} />; // inner spacer
          const code = isCenter ? '' : ep;
          const active = cur === code;
          return (
            <Box key={`${r}-${c}`} onClick={() => onPick(code)}
              title={isCenter ? 'Авто (ближайшая точка)' : 'Точка на периметре'}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                border: '1px solid', borderColor: active ? 'primary.main' : 'divider', borderRadius: '50%',
                bgcolor: active ? 'primary.main' : 'transparent', color: active ? '#fff' : 'text.secondary',
                fontSize: 11, lineHeight: 1, userSelect: 'none',
                '&:hover': { borderColor: 'primary.main', bgcolor: active ? 'primary.main' : 'action.hover' },
              }}>
              {isCenter ? '⊙' : '•'}
            </Box>
          );
        }))}
      </Box>
    </Box>
  );
}
function ConnectionPanel({ c, onClose, onAddBend, onStraighten, onSetAnchor }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Связь</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Chip size="small" label={c.type_name || 'связь'} sx={{ mt: 0.5, bgcolor: c.color, color: '#fff' }} />
      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={1}>
        {c.subnet_name && <Row label="Подсеть" value={c.subnet_name} />}
        {c.vlan && <Row label="VLAN" value={c.vlan} />}
        {c.protocol && <Row label="Протокол" value={c.protocol} />}
        {c.source_interface && <Row label="Интерфейс (источник)" value={c.source_interface} />}
        {c.target_interface && <Row label="Интерфейс (приёмник)" value={c.target_interface} />}
        {c.comment && <Row label="Комментарий" value={c.comment} />}
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Форма линии</Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" onClick={onAddBend}>+ Точка сгиба</Button>
        <Button size="small" variant="outlined" onClick={onStraighten}>Выпрямить</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Добавь точку и перетащи оранжевый кружок на карте, чтобы согнуть линию. Двойной клик по линии — тоже добавляет точку, ПКМ по кружку — убрать.
      </Typography>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Точки подключения</Typography>
      <Stack direction="row" spacing={2} justifyContent="space-around">
        <AnchorCompass title="Источник" value={c.source_handle} onPick={(code) => onSetAnchor('source', code)} />
        <AnchorCompass title="Приёмник" value={c.target_handle} onPick={(code) => onSetAnchor('target', code)} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        16 точек по периметру (4 угла + по 3 на сторону) — выбери, куда крепить линию, чтобы разводить связи красиво. ⊙ в центре — авто (ближайшая точка).
      </Typography>
    </Box>
  );
}
function Row({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{String(value)}</Typography>
    </Box>
  );
}
